import { useState, useEffect, useCallback } from "react";
import { paymentsAPI, patientsAPI, exportAPI } from "@/lib/api";
import { validate, paymentSchema } from "@/lib/validations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, CreditCard, DollarSign, TrendingUp, Download, Filter } from "lucide-react";
import { toast } from "sonner";

const METHOD_LABELS = {
  cash: "Efectivo",
  card: "Tarjeta",
  transfer: "Transferencia",
};

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ patient_id: "", amount: "", method: "cash", notes: "" });
  const [saving, setSaving] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [errors, setErrors] = useState({});

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await paymentsAPI.list(params);
      setPayments(res.data.payments || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Error cargando pagos");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchPayments();
    patientsAPI.list({ limit: 200 }).then((r) => setPatients(r.data.patients || [])).catch(() => {});
  }, [fetchPayments]);

  const handleExportCSV = async () => {
    try {
      const res = await exportAPI.payments();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "pagos.csv";
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Exportacion descargada");
    } catch {
      toast.error("Error al exportar");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const v = validate(paymentSchema, { ...form, amount: parseFloat(form.amount) || 0 });
    if (!v.ok) {
      setErrors(v.errors);
      const firstErr = Object.values(v.errors)[0];
      toast.error(firstErr);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await paymentsAPI.create({ ...form, amount: parseFloat(form.amount) });
      toast.success("Pago registrado");
      setShowNew(false);
      setForm({ patient_id: "", amount: "", method: "cash", notes: "" });
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally {
      setSaving(false);
    }
  };

  const totalAmount = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Pagos</h1>
          <p className="text-muted-foreground text-sm mt-0.5">{total} pago{total !== 1 ? "s" : ""} registrado{total !== 1 ? "s" : ""}</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-full" onClick={handleExportCSV} data-testid="export-payments-btn">
          <Download className="w-4 h-4 mr-1.5" />Exportar CSV
        </Button>
        <Button size="sm" className="rounded-full" onClick={() => setShowNew(true)} data-testid="new-payment-btn">
          <Plus className="w-4 h-4 mr-1.5" />Registrar pago
        </Button>
      </div>

      {/* Date Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Filtrar:</span>
        </div>
        <Input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="w-40 h-8 text-xs"
          placeholder="Desde"
          data-testid="payments-from-date"
        />
        <Input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="w-40 h-8 text-xs"
          placeholder="Hasta"
          data-testid="payments-to-date"
        />
        {(fromDate || toDate) && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setFromDate(""); setToDate(""); }} data-testid="clear-date-filter">
            Limpiar
          </Button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-5 flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total cobrado</p>
              <p className="text-2xl font-bold mt-1">${totalAmount.toLocaleString("es-MX")}</p>
            </div>
            <div className="p-2.5 rounded-xl bg-muted text-green-600 dark:text-green-400">
              <DollarSign className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-5 flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Numero de pagos</p>
              <p className="text-2xl font-bold mt-1">{total}</p>
            </div>
            <div className="p-2.5 rounded-xl bg-muted text-primary">
              <CreditCard className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-5 flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Promedio por pago</p>
              <p className="text-2xl font-bold mt-1">${total > 0 ? Math.round(totalAmount / total).toLocaleString("es-MX") : 0}</p>
            </div>
            <div className="p-2.5 rounded-xl bg-muted text-secondary">
              <TrendingUp className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payments List */}
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : payments.length > 0 ? (
        <div className="space-y-2">
          {payments.map((p) => (
            <Card key={p.id} data-testid={`payment-row-${p.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{p.patient_name || "Paciente"}</p>
                  <p className="text-xs text-muted-foreground">{p.created_at?.split("T")[0]} &middot; {METHOD_LABELS[p.method] || p.method}</p>
                </div>
                {p.notes && <p className="text-xs text-muted-foreground hidden sm:block max-w-[200px] truncate">{p.notes}</p>}
                <p className="text-sm font-bold text-foreground">${p.amount?.toLocaleString("es-MX")}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">Sin pagos registrados</p>
          <Button variant="link" size="sm" className="mt-1" onClick={() => setShowNew(true)}>Registrar primer pago</Button>
        </div>
      )}

      {/* New Payment Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-md" data-testid="new-payment-dialog">
          <DialogHeader>
            <DialogTitle>Registrar pago</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Paciente *</Label>
              <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                <SelectTrigger data-testid="payment-patient-select">
                  <SelectValue placeholder="Seleccionar paciente" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Monto *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
                required
                data-testid="payment-amount"
              />
            </div>
            <div>
              <Label>Metodo de pago</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Efectivo</SelectItem>
                  <SelectItem value="card">Tarjeta</SelectItem>
                  <SelectItem value="transfer">Transferencia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="Concepto o notas adicionales..."
                data-testid="payment-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" disabled={saving} data-testid="payment-save-btn">
                {saving ? "Guardando..." : "Registrar pago"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
